// Lab 3 deterministic scenarios: election, replication, loss, crash and
// recovery with value adoption, stale ballots, partitions, ordered execution,
// compaction, duplicates, and lagging recovery.
import { defaultConfig } from './model';

const FIRE = (to, key) => ({ op: 'fire', select: { kind: 'timer', to, timerKey: key } });
const CHECK = (s) => FIRE(s, 'check');
const PINGT = (s) => FIRE(s, 'ping');
const ACCEPTT = (s) => FIRE(s, 'accept');
const RETRY = (seq) => FIRE('C1', 'retry:' + seq);
const PREPARE = (f, t) => ({ op: 'deliver', select: { kind: 'message', from: f, to: t, msgType: 'prepare' } });
const PROMISE = (f, t) => ({ op: 'deliver', select: { kind: 'message', from: f, to: t, msgType: 'promise' } });
const ACCEPT = (f, t) => ({ op: 'deliver', select: { kind: 'message', from: f, to: t, msgType: 'accept' } });
const ACCEPTED = (f, t) => ({ op: 'deliver', select: { kind: 'message', from: f, to: t, msgType: 'accepted' } });
const PING = (f, t) => ({ op: 'deliver', select: { kind: 'message', from: f, to: t, msgType: 'ping' } });
const PINGREPLY = (f, t) => ({ op: 'deliver', select: { kind: 'message', from: f, to: t, msgType: 'pingreply' } });
const CREQUEST = (t) => ({ op: 'deliver', select: { kind: 'message', to: t, msgType: 'crequest' } });
const CREPLY = () => ({ op: 'deliver', select: { kind: 'message', msgType: 'creply' } });
const INVOKE = (command) => ({ op: 'submit', to: 'C1', command, label: 'invoke' });
const CMD = () => ({ op: 'deliver', select: { kind: 'clientCommand', to: 'C1' } });
const QUIESCE = { op: 'advance', to: 2000 };

const PUT_X = { op: 'put', key: 'x', value: 1 };
const PUT_Y = { op: 'put', key: 'y', value: 2 };
const PUT_Z = { op: 'put', key: 'z', value: 3 };

export function electS1() {
  return [
    { op: 'advance', to: 10 },
    CHECK('S1'),
    PREPARE('S1', 'S2'), PREPARE('S1', 'S3'),
    PROMISE('S2', 'S1'),
    PROMISE('S3', 'S1'),
    PING('S1', 'S2'), PING('S1', 'S3'),
    PINGREPLY('S2', 'S1'), PINGREPLY('S3', 'S1'),
  ];
}

// One full client operation through leader S1, ending with every replica
// executed and the heartbeat replies consumed.
export function replicateViaS1(command) {
  return [
    INVOKE(command), CMD(), CREQUEST('S1'),
    ACCEPT('S1', 'S2'), ACCEPT('S1', 'S3'),
    ACCEPTED('S2', 'S1'), ACCEPTED('S3', 'S1'),
    CREPLY(),
    PING('S1', 'S2'), PING('S1', 'S3'),
    PINGREPLY('S2', 'S1'), PINGREPLY('S3', 'S1'),
  ];
}

export function leaderElection() {
  return { config: defaultConfig(), seed: 1, decisions: electS1() };
}

export function normalReplication() {
  return { config: defaultConfig(), seed: 2, decisions: [...electS1(), ...replicateViaS1(PUT_X)] };
}

export function lostPhase2() {
  return {
    config: defaultConfig(),
    seed: 3,
    decisions: [
      ...electS1(),
      INVOKE(PUT_X), CMD(), CREQUEST('S1'),
      { ...ACCEPT('S1', 'S2'), op: 'drop' },
      { ...ACCEPT('S1', 'S3'), op: 'drop' },
      ACCEPTT('S1'),
      ACCEPT('S1', 'S2'), ACCEPT('S1', 'S3'),
      ACCEPTED('S2', 'S1'), ACCEPTED('S3', 'S1'),
      CREPLY(),
      PING('S1', 'S2'), PING('S1', 'S3'),
      PINGREPLY('S2', 'S1'), PINGREPLY('S3', 'S1'),
    ],
  };
}

export function leaderCrashRecovery() {
  return {
    config: defaultConfig(),
    seed: 4,
    decisions: [
      ...electS1(),
      ...replicateViaS1(PUT_X),
      INVOKE(PUT_Y), CMD(), CREQUEST('S1'),
      ACCEPT('S1', 'S2'),
      { op: 'crash', node: 'S1' },
      QUIESCE,
      CHECK('S2'),
      PREPARE('S2', 'S3'),
      PROMISE('S3', 'S2'),
      PING('S2', 'S3'), PINGREPLY('S3', 'S2'),
      ACCEPT('S2', 'S3'),
      ACCEPTED('S3', 'S2'),
      PING('S2', 'S3'), PINGREPLY('S3', 'S2'),
      RETRY(2), RETRY(2), RETRY(2),
      CREQUEST('S2'),
      CREPLY(),
      { op: 'restart', node: 'S1' },
      PINGT('S2'),
      PING('S2', 'S1'), PING('S2', 'S1'), PING('S2', 'S1'),
      PING('S2', 'S3'),
      PINGREPLY('S1', 'S2'), PINGREPLY('S1', 'S2'), PINGREPLY('S1', 'S2'),
      PINGREPLY('S3', 'S2'),
    ],
  };
}

export function staleBallotRejected() {
  return {
    config: defaultConfig(),
    seed: 5,
    decisions: [
      ...electS1(),
      { op: 'partition', setA: ['S1'], setB: ['S2', 'S3', 'C1'] },
      QUIESCE,
      CHECK('S2'),
      PREPARE('S2', 'S3'),
      PROMISE('S3', 'S2'),
      PING('S2', 'S3'), PINGREPLY('S3', 'S2'),
      INVOKE(PUT_X), CMD(),
      { op: 'heal', setA: ['S1'], setB: ['S2', 'S3', 'C1'] },
      CREQUEST('S1'),
      ACCEPT('S1', 'S2'),
      PINGT('S2'),
      PING('S2', 'S1'), PING('S2', 'S3'),
      PINGREPLY('S1', 'S2'), PINGREPLY('S3', 'S2'),
      RETRY(1), RETRY(1), RETRY(1),
      CREQUEST('S2'),
      ACCEPT('S2', 'S3'),
      ACCEPTED('S3', 'S2'),
      CREPLY(),
      PING('S2', 'S1'), PING('S2', 'S1'), PING('S2', 'S3'),
      PINGREPLY('S1', 'S2'), PINGREPLY('S1', 'S2'), PINGREPLY('S3', 'S2'),
    ],
  };
}

export function minorityStall() {
  return {
    config: defaultConfig(),
    seed: 6,
    decisions: [
      ...electS1(),
      { op: 'partition', setA: ['S1'], setB: ['S2', 'S3'] },
      INVOKE(PUT_X), CMD(), CREQUEST('S1'),
      ACCEPTT('S1'),
      { op: 'heal', setA: ['S1'], setB: ['S2', 'S3'] },
      ACCEPT('S1', 'S2'), ACCEPT('S1', 'S3'),
      ACCEPTED('S2', 'S1'), ACCEPTED('S3', 'S1'),
      CREPLY(),
      PING('S1', 'S2'), PING('S1', 'S3'),
      PINGREPLY('S2', 'S1'), PINGREPLY('S3', 'S1'),
    ],
  };
}

export function orderedExecution() {
  return {
    config: defaultConfig(),
    seed: 7,
    decisions: [
      ...electS1(),
      ...replicateViaS1(PUT_X),
      ...replicateViaS1(PUT_Y),
      ...replicateViaS1(PUT_Z),
    ],
  };
}

export function logCompaction() {
  return {
    config: defaultConfig(),
    seed: 8,
    decisions: [
      ...electS1(),
      ...replicateViaS1(PUT_X),
      ...replicateViaS1(PUT_Y),
      ...replicateViaS1(PUT_Z),
      PINGT('S1'),
      PING('S1', 'S2'), PING('S1', 'S3'),
      PINGREPLY('S2', 'S1'), PINGREPLY('S3', 'S1'),
    ],
  };
}

export function duplicateClientRequest() {
  return {
    config: defaultConfig(),
    seed: 9,
    decisions: [
      ...electS1(),
      INVOKE(PUT_X), CMD(),
      { ...CREQUEST('S1'), op: 'duplicate' },
      CREQUEST('S1'), CREQUEST('S1'),
      ACCEPT('S1', 'S2'), ACCEPT('S1', 'S3'),
      ACCEPTED('S2', 'S1'), ACCEPTED('S3', 'S1'),
      CREPLY(),
      PING('S1', 'S2'), PING('S1', 'S3'),
      PINGREPLY('S2', 'S1'), PINGREPLY('S3', 'S1'),
    ],
  };
}

export function laggingRecovery() {
  return {
    config: defaultConfig(),
    seed: 10,
    decisions: [
      ...electS1(),
      ...replicateViaS1(PUT_X),
      { op: 'crash', node: 'S1', erase: true },
      { op: 'restart', node: 'S1' },
      QUIESCE,
      CHECK('S1'),
      PREPARE('S1', 'S2'),
      PROMISE('S2', 'S1'),
      CHECK('S2'),
      PREPARE('S2', 'S3'),
      PROMISE('S3', 'S2'),
      PING('S2', 'S3'), PINGREPLY('S3', 'S2'),
      PING('S2', 'S1'), PINGREPLY('S1', 'S2'),
      INVOKE(PUT_Y), CMD(),
      RETRY(2), RETRY(2), RETRY(2),
      CREQUEST('S2'),
      ACCEPT('S2', 'S3'),
      ACCEPTED('S3', 'S2'),
      CREPLY(),
      PING('S2', 'S1'), PING('S2', 'S3'),
      PINGREPLY('S1', 'S2'), PINGREPLY('S3', 'S2'),
    ],
  };
}

export const ALL_SCENARIOS = {
  leaderElection,
  normalReplication,
  lostPhase2,
  leaderCrashRecovery,
  staleBallotRejected,
  minorityStall,
  orderedExecution,
  logCompaction,
  duplicateClientRequest,
  laggingRecovery,
};
